import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Download, X, Calendar, User, Clock, MapPin } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const PhotoModal = ({ isOpen, onClose, record }) => {
  if (!record) return null;
  const [isDownloading, setIsDownloading] = React.useState(false);

  const formatTimestamp = (timestamp) => {
    let timeStr, dateStr;
    
    // Handle timezone correctly (same logic as AdminDashboard)
    if (timestamp.includes('+08:00')) {
      const parts = timestamp.split('T');
      dateStr = parts[0];
      timeStr = parts[1].split('+')[0];
    } else {
      const utcTime = new Date(timestamp + 'Z');
      const taipeiTime = new Date(utcTime.getTime() + (8 * 60 * 60 * 1000));
      const isoString = taipeiTime.toISOString();
      dateStr = isoString.split('T')[0];
      timeStr = isoString.split('T')[1].split('.')[0];
    }
    
    const [year, month, day] = dateStr.split('-');
    const [hours, minutes, seconds] = timeStr.split(':');
    
    // Format date
    const date = new Date(year, month - 1, day);
    const formattedDate = date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
    
    // Format time to 12-hour format
    const hour24 = parseInt(hours);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    const formattedTime = `${hour12.toString().padStart(2, '0')}:${minutes}:${seconds} ${ampm}`;
    
    return `${formattedDate} ${formattedTime}`;
  };

  const handleDownload = async () => {
    if (!record.photo_url || isDownloading) return;

    try {
      setIsDownloading(true);

      const backendUrlRaw = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL || '';
      const backendUrl = backendUrlRaw.replace(/\/$/, '');

      const match = record.photo_url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
      const downloadUrl = match && match[1] ? `${backendUrl}/api/proxy-image/${match[1]}` : record.photo_url;

      const response = await fetch(downloadUrl, { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const safeName = (record.user_name || 'user').toString().trim().replace(/[^\w.-]+/g, '_');
      const datePart = (record.timestamp || '').toString().split('T')[0] || new Date().toISOString().split('T')[0];
      const ext =
        blob.type === 'image/png' ? 'png' :
        blob.type === 'image/webp' ? 'webp' :
        blob.type === 'image/gif' ? 'gif' :
        'jpg';

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `attendance_${safeName}_${datePart}.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Download failed",
        description: "Unable to download the attendance photo. Please try again.",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <User className="h-5 w-5 mr-2" />
              Attendance Photo - {record.user_name}
            </span>
            {record.photo_url && (
              <Button
                onClick={handleDownload}
                disabled={isDownloading}
                size="sm"
                variant="outline"
                className="ml-2"
              >
                <Download className="h-4 w-4 mr-1" />
                {isDownloading ? 'Downloading...' : 'Download'}
              </Button>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">Attendance record photo preview and details</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Photo Display */}
          <div className="relative bg-gray-100 rounded-lg overflow-hidden">
            {record.photo_url ? (
              <img 
                src={(() => {
                  // Extract file_id from Google Drive URL and use proxy
                  const match = record.photo_url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
                  if (match && match[1]) {
                    const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
                    return `${backendUrl}/api/proxy-image/${match[1]}`;
                  }
                  // Fallback to original URL
                  return record.photo_url;
                })()}
                alt={`Attendance photo for ${record.user_name}`}
                className="w-full h-auto max-h-96 object-contain"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y5ZmFmYiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LWZhbWlseT0ic2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiI+SW1hZ2UgTm90IEF2YWlsYWJsZTwvdGV4dD48L3N2Zz4=';
                }}
              />
            ) : (
              <div className="w-full h-48 flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <User className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No photo available</p>
                </div>
              </div>
            )}
          </div>

          {/* Attendance Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="space-y-3">
              <div className="flex items-center text-sm">
                <Clock className="h-4 w-4 mr-2 text-gray-500" />
                <span className="font-medium">Time:</span>
                <span className="ml-2">{formatTimestamp(record.timestamp)}</span>
              </div>
              
              <div className="flex items-center text-sm">
                <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                <span className="font-medium">Type:</span>
                <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                  record.type === 'time_in' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {record.type.replace('_', ' ').toUpperCase()}
                </span>
              </div>

              {record.status && (
                <div className="flex items-center text-sm">
                  <span className="font-medium">Status:</span>
                  <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                    record.status === 'on_time' ? 'bg-green-100 text-green-800' :
                    record.status === 'late' ? 'bg-orange-100 text-orange-800' :
                    record.status === 'undertime' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {record.status === 'on_time' ? 'ON TIME' :
                     record.status === 'late' ? 'LATE' :
                     record.status === 'undertime' ? 'UNDERTIME' :
                     record.status?.toUpperCase() || 'N/A'}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-3">
              {record.location && (
                <div className="flex items-start text-sm">
                  <MapPin className="h-4 w-4 mr-2 text-gray-500 mt-0.5" />
                  <div>
                    <span className="font-medium">Location:</span>
                    <div className="ml-0 mt-1 text-xs text-gray-600">
                      <div>{record.location.address}</div>
                      <div>Lat: {record.location.latitude?.toFixed(6)}</div>
                      <div>Lng: {record.location.longitude?.toFixed(6)}</div>
                    </div>
                  </div>
                </div>
              )}

              {record.comment && (
                <div className="text-sm">
                  <span className="font-medium">Comment:</span>
                  <div className="mt-1 p-2 bg-blue-50 rounded text-blue-800 text-xs">
                    {record.comment}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PhotoModal;
